// frontend/packages/business-console/src/pages/ContentEditor.jsx

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Typography } from '@mui/material';

import { useLocation } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import { BusinessContext } from '../contexts/BusinessContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import FileList from '../components/content/FileList';
import PageTrafficPanel from '../components/content/PageTrafficPanel';
import EditorPane from '../components/content/EditorPane';
import MobileEditorPane from '../components/content/MobileEditorPane';
import EditorInsightsRail from '../components/content/EditorInsightsRail';
import WelcomeChecklist from '../components/help/WelcomeChecklist';
import useAutosave from '../hooks/useAutosave';
import useMobileDetect from '../hooks/useMobileDetect';

import {
  buildFrontmatterYaml,
  parseFrontmatterYaml,
  recombine,
  splitFrontmatter,
  contentSitePath,
  setAtPath,
} from '../utils/contentEditorHelpers';

import {
  deleteContentFile,
  getContentFile,
  getContentSchema,
  listContentFiles,
  saveContentFile,
  getPublishStatus,
  publishDraftToProd,
  restoreDraftToLastPublish,
} from '../utils/contentApi';
import { getSitePreviewUrls } from '../services/sitePreviewService';
import {
  useTokens,
  PageHeader,
  MetricBarCards,
} from '../components/primitives';
import { getPageInsights, getPageSeo } from '../services/visitorsService';

export default function ContentEditor({ embedded = false }) {
  const t = useTokens();
  const location = useLocation();
  const isDemo = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    if (params.get('demo') === '1') return true;
    try {
      return localStorage.getItem('pulse.demo') === '1';
    } catch {
      return false;
    }
  }, [location.search]);
  const { businessId, ensureSignedIn } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { business } = useContext(BusinessContext);

  // Done-for-you implementation (auto-applying AI fixes) is a The Ryzze tier
  // benefit. Copilot authoring stays available to everyone; only the
  // one-click "Fix with AI" apply path on the SEO rail is gated. Lower tiers
  // still see the finding, with a nudge that it can be done for them.
  const canAutoFix = Boolean(business?.entitlements?.doneForYouImplementation);

  // Hub stats (real aggregates): views + leads from page-insights, average
  // on-page SEO from page-seo. Best-effort; absent until there's data.
  const [hubStats, setHubStats] = useState(null);
  // Per-site-path metrics map { path -> { views, leads, onPageScore, health } }
  // for joining page analytics to the editable file list (heuristic path map).
  const [metricsByPath, setMetricsByPath] = useState({});

  // list state
  const [files, setFiles] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState('');

  // editor state
  const [mode, setMode] = useState('none'); // none | edit | create
  const [selected, setSelected] = useState(null);
  const [schemaFields, setSchemaFields] = useState(null);

  const [fileSha, setFileSha] = useState('');
  const [frontmatterObj, setFrontmatterObj] = useState({});
  const [markdownBody, setMarkdownBody] = useState('');
  const [originalCombined, setOriginalCombined] = useState('');

  const [showAllOptional, setShowAllOptional] = useState(false);

  const [fileError, setFileError] = useState('');
  const [loadingFile, setLoadingFile] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // publish status
  const [publishStatus, setPublishStatus] = useState(null);

  // preview URLs (uat/prod) for the active business — surfaced in the editor
  // when there are unpublished draft changes.
  const [previewUrls, setPreviewUrls] = useState({
    uatUrl: null,
    prodUrl: null,
  });

  // create state
  const [createCollection, setCreateCollection] = useState('');

  const schemaCacheRef = useRef({});
  const fileCacheRef = useRef({});

  const combinedCurrent = useMemo(() => {
    const fmText = buildFrontmatterYaml(frontmatterObj);
    return recombine(fmText, markdownBody);
  }, [frontmatterObj, markdownBody]);

  const dirty = useMemo(
    () => combinedCurrent !== originalCombined,
    [combinedCurrent, originalCombined]
  );

  const { isMobile } = useMobileDetect();
  useAutosave({
    filePath: selected?.path,
    content: combinedCurrent,
    enabled: mode === 'edit',
  });

  const requireAuth = useCallback(async () => {
    if (!businessId)
      throw new Error('No business associated with your account.');
    const ok = await ensureSignedIn();
    if (!ok) throw new Error('Not signed in. Please sign in again.');
    return String(businessId);
  }, [businessId, ensureSignedIn]);

  const refreshPublishStatus = useCallback(async () => {
    try {
      await requireAuth();
      const s = await getPublishStatus();
      setPublishStatus(s);
    } catch {
      setPublishStatus(null);
    }
  }, [requireAuth]);

  const resetEditorState = useCallback(() => {
    setMode('none');
    setSelected(null);
    setSchemaFields(null);
    setFileSha('');
    setFrontmatterObj({});
    setMarkdownBody('');
    setOriginalCombined('');
    setCreateCollection('');
  }, []);

  const refreshList = useCallback(async () => {
    setListError('');
    setLoadingList(true);
    try {
      await requireAuth();
      const data = await listContentFiles('uat');
      setFiles(data);
      await refreshPublishStatus();
      return data;
    } catch (e) {
      setListError(e?.message || String(e));
      return [];
    } finally {
      setLoadingList(false);
    }
  }, [refreshPublishStatus, requireAuth]);

  useEffect(() => {
    refreshList();
  }, [refreshList]);

  // Load preview URLs once per business.
  useEffect(() => {
    let alive = true;
    if (!businessId) {
      setPreviewUrls({ uatUrl: null, prodUrl: null });
      return undefined;
    }
    (async () => {
      try {
        await requireAuth();
        const urls = await getSitePreviewUrls();
        if (alive) setPreviewUrls(urls);
      } catch {
        if (alive) setPreviewUrls({ uatUrl: null, prodUrl: null });
      }
    })();
    return () => {
      alive = false;
    };
  }, [businessId, requireAuth]);

  // Load hub aggregates once per business.
  useEffect(() => {
    if (!businessId) return undefined;
    let alive = true;
    Promise.all([
      getPageInsights(30).catch(() => ({ pages: [] })),
      getPageSeo().catch(() => ({ pages: [] })),
    ]).then(([ins, seo]) => {
      if (!alive) return;
      const pages = Array.isArray(ins?.pages) ? ins.pages : [];
      const seoPages = Array.isArray(seo?.pages) ? seo.pages : [];
      const views = pages.reduce((s, p) => s + (p.views || 0), 0);
      const leads = pages.reduce((s, p) => s + (p.leads || 0), 0);
      const scores = seoPages
        .map((p) => p.onPageScore)
        .filter((n) => typeof n === 'number');
      const avgSeo = scores.length
        ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
        : null;
      setHubStats({ views, leads, avgSeo });

      // Per-path map so each file row can show its own views/leads/SEO.
      const byPath = {};
      for (const p of pages) {
        if (!p?.path) continue;
        byPath[p.path] = {
          ...(byPath[p.path] || {}),
          views: p.views,
          leads: p.leads,
        };
      }
      for (const p of seoPages) {
        if (!p?.path) continue;
        byPath[p.path] = {
          ...(byPath[p.path] || {}),
          onPageScore: p.onPageScore,
          health: p.health,
          issues: Array.isArray(p.issues) ? p.issues : [],
        };
      }
      setMetricsByPath(byPath);
    });
    return () => {
      alive = false;
    };
  }, [businessId]);

  const loadSchema = useCallback(async (collection) => {
    if (!collection) return null;
    if (schemaCacheRef.current?.[collection])
      return schemaCacheRef.current[collection];

    const fields = await getContentSchema(collection, 'uat');
    schemaCacheRef.current = {
      ...(schemaCacheRef.current || {}),
      [collection]: fields,
    };
    return fields;
  }, []);

  const openFile = useCallback(
    async (file) => {
      setFileError('');
      setLoadingFile(true);

      try {
        await requireAuth();
        const path = file?.path || '';
        const collection = file?.collection || '';

        setMode('edit');
        setSelected(file);
        setCreateCollection('');

        const fieldsPromise = loadSchema(collection);
        let filePromise;
        if (fileCacheRef.current[path]) {
          filePromise = Promise.resolve(fileCacheRef.current[path]);
        } else {
          filePromise = getContentFile(path, 'uat').then((res) => {
            fileCacheRef.current[path] = res;
            return res;
          });
        }

        const [fields, { sha, content }] = await Promise.all([
          fieldsPromise,
          filePromise,
        ]);
        setSchemaFields(fields);
        setFileSha(sha);

        const parts = splitFrontmatter(content);
        const fmObj = parseFrontmatterYaml(parts.frontmatter);

        setFrontmatterObj(fmObj);
        setMarkdownBody(parts.body);

        const normalizedFm = buildFrontmatterYaml(fmObj);
        const normalizedCombined = recombine(normalizedFm, parts.body);
        setOriginalCombined(normalizedCombined);
      } catch (e) {
        setFileError(e?.message || String(e));
        resetEditorState();
        setFileSha('');
      } finally {
        setLoadingFile(false);
      }
    },
    [loadSchema, requireAuth, resetEditorState]
  );

  const startCreate = useCallback(
    async (collection) => {
      setFileError('');
      setMode('create');
      setSelected(null);
      setCreateCollection(collection);
      setFileSha('');
      setShowAllOptional(false);

      try {
        await requireAuth();
        const fields = await loadSchema(collection);
        setSchemaFields(fields);

        const draft = {};
        if (fields) {
          for (const [key, spec] of Object.entries(fields)) {
            if (spec?.hasDefault) draft[key] = spec.default;
            else if (spec?.required)
              draft[key] = spec.type === 'array' ? [] : '';
          }
        }

        setFrontmatterObj(draft);
        setMarkdownBody('');
        setOriginalCombined('');
      } catch (e) {
        setFileError(e?.message || String(e));
      }
    },
    [loadSchema, requireAuth]
  );

  const handleSaveDraft = useCallback(
    async ({ mode: saveMode, path }) => {
      setFileError('');
      setSaving(true);

      try {
        await requireAuth();
        const p = String(path || '').trim();
        if (!p) throw new Error('Missing file path.');

        await saveContentFile(
          p,
          combinedCurrent,
          saveMode === 'edit' ? fileSha : '',
          'uat'
        );

        if (fileCacheRef.current[p]) {
          delete fileCacheRef.current[p];
        }

        await refreshList();

        const created = {
          id: p,
          path: p,
          name: p.split('/').pop() || p,
          collection:
            saveMode === 'create'
              ? createCollection
              : selected?.collection || '',
        };

        await openFile(created);
        showSnackbar('Draft saved', 'success');
        return true;
      } catch (e) {
        setFileError(e?.message || String(e));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [
      combinedCurrent,
      createCollection,
      fileSha,
      openFile,
      refreshList,
      requireAuth,
      selected?.collection,
      showSnackbar,
    ]
  );

  const [publishSuccess, setPublishSuccess] = useState(false);
  // Bumped by the publish keyboard shortcut. The editor pane watches it and
  // opens the same confirmation dialog the Publish button opens: the shortcut
  // used to call handlePublish directly, so a stray Ctrl/Cmd+Shift+P pushed
  // the whole draft branch to the live site with nothing asked and nothing
  // undoable.
  const [publishConfirmSignal, setPublishConfirmSignal] = useState(0);

  const handlePublish = useCallback(async () => {
    setFileError('');
    setPublishSuccess(false);
    setPublishing(true);
    try {
      await requireAuth();

      if (dirty) throw new Error('Save your draft before publishing.');

      await publishDraftToProd();
      await refreshPublishStatus();
      setPublishSuccess(true);
      showSnackbar(
        'Published! Changes are deploying to your live site.',
        'success'
      );
    } catch (e) {
      setFileError(e?.message || String(e));
    } finally {
      setPublishing(false);
    }
  }, [dirty, refreshPublishStatus, requireAuth, showSnackbar]);

  const handleRestoreToLastPublish = useCallback(async () => {
    setFileError('');
    setPublishing(true);
    try {
      await requireAuth();
      await restoreDraftToLastPublish();
      schemaCacheRef.current = {};

      const refreshedFiles = await refreshList();

      if (selected?.path && selected?.collection) {
        const reopened = (refreshedFiles || []).find(
          (f) =>
            f?.path === selected.path && f?.collection === selected.collection
        );

        if (reopened) await openFile(reopened);
        else resetEditorState();
      } else {
        resetEditorState();
      }
    } catch (e) {
      setFileError(e?.message || String(e));
    } finally {
      setPublishing(false);
    }
  }, [openFile, refreshList, requireAuth, resetEditorState, selected]);

  const handleDelete = useCallback(async () => {
    if (!selected?.path) return;

    setFileError('');
    setDeleting(true);

    try {
      await requireAuth();
      await deleteContentFile(selected.path, fileSha, 'uat');

      if (fileCacheRef.current[selected.path]) {
        delete fileCacheRef.current[selected.path];
      }

      await refreshList();

      resetEditorState();
      showSnackbar('File deleted', 'info');
    } catch (e) {
      setFileError(e?.message || String(e));
    } finally {
      setDeleting(false);
    }
  }, [
    fileSha,
    refreshList,
    requireAuth,
    resetEditorState,
    selected,
    showSnackbar,
  ]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const mod = e.ctrlKey || e.metaKey;

      if (mod && !e.shiftKey && e.key === 's') {
        e.preventDefault();
        if (mode !== 'edit' || !dirty || saving || !selected?.path) return;
        handleSaveDraft({ mode: 'edit', path: selected.path });
      }

      if (mod && e.shiftKey && (e.key === 'p' || e.key === 'P')) {
        e.preventDefault();
        if (dirty || saving || publishing) return;
        if (Number(publishStatus?.ahead_by ?? 0) <= 0) return;
        setPublishConfirmSignal((n) => n + 1);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    dirty,
    handleSaveDraft,
    mode,
    publishStatus?.ahead_by,
    publishing,
    saving,
    selected?.path,
  ]);

  const editorProps = {
    mode,
    selectedFile: selected,
    schemaFields,
    frontmatterObj,
    markdownBody,
    showAllOptional,
    dirty,
    loadingFile,
    saving,
    deleting,
    publishing,
    publishStatus,
    previewUrls,
    publishSuccess,
    publishConfirmSignal,
    onDismissPublishSuccess: () => setPublishSuccess(false),
    error: fileError,
    createCollection,
    onToggleShowAllOptional: setShowAllOptional,
    onFrontmatterChange: setFrontmatterObj,
    onMarkdownChange: setMarkdownBody,
    onSaveDraft: handleSaveDraft,
    onPublish: handlePublish,
    onRestoreToLastPublish: handleRestoreToLastPublish,
    onDelete: handleDelete,
  };

  // Demo mode (shared `?demo=1` / `pulse.demo` flag): synthesize per-page
  // metrics for the real content files so the Content hub + editor rail
  // present fully populated for client demos, the same way Pulse / Analytics
  // / Payments fill from fixtures. Numbers are plausible, not path-dependent.
  const demoContent = useMemo(() => {
    if (!isDemo) return null;
    const V = [2100, 1300, 860, 540, 320, 210, 140];
    const L = [0, 7, 11, 0, 3, 1, 0];
    const S = [92, 86, 78, 71, 64, 88, 80];
    const byPath = {};
    let views = 0;
    let leads = 0;
    const scores = [];
    (files || []).forEach((f, i) => {
      const p = contentSitePath(f);
      if (!p) return;
      const v = V[i % V.length];
      const l = L[i % L.length];
      const s = S[i % S.length];
      byPath[p] = {
        views: v,
        leads: l,
        onPageScore: s,
        health: s >= 80 ? 'good' : s >= 50 ? 'warn' : 'bad',
        issues: s >= 86 ? [] : ['Add 1 internal link'],
      };
      views += v;
      leads += l;
      scores.push(s);
    });
    const avgSeo = scores.length
      ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
      : null;
    return { metricsByPath: byPath, hubStats: { views, leads, avgSeo } };
  }, [isDemo, files]);

  const effMetricsByPath = demoContent
    ? demoContent.metricsByPath
    : metricsByPath;
  const effHubStats = demoContent ? demoContent.hubStats : hubStats;

  const aheadBy = isDemo ? 2 : Number(publishStatus?.ahead_by ?? 0);
  const statCards = [
    { label: 'Pages', value: files.length, bar: 'blue' },
    {
      label: 'Unpublished',
      value: aheadBy,
      bar: aheadBy > 0 ? 'amber' : 'grayBorder',
    },
    {
      label: 'Views · 30d',
      value: (effHubStats?.views ?? 0).toLocaleString(),
      bar: 'blue',
    },
    { label: 'Leads · 30d', value: effHubStats?.leads ?? 0, bar: 'green' },
    {
      label: 'SEO health',
      value: effHubStats?.avgSeo != null ? effHubStats.avgSeo : '-',
      sub: 'avg on-page',
      gradient: true,
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: { md: '100%' },
        minHeight: 0,
        width: '100%',
      }}
    >
      {/* Hub header + stat strip (real aggregates). When embedded in the
          Content workspace tabs, the workspace owns the "Content" header, so
          we drop ours and keep just the stat strip. */}
      <Box sx={{ px: 3, pt: embedded ? 1.5 : 2.5, pb: 1.5 }}>
        {!embedded ? (
          <>
            <PageHeader title="Content" />
            <Typography sx={{ fontSize: 13.5, color: t.muted, mt: 0.5 }}>
              Your website pages and posts: edit, publish, and see what each one
              is doing.
            </Typography>
          </>
        ) : null}
        {!loadingList ? (
          <MetricBarCards cards={statCards} sx={{ mt: embedded ? 0 : 2 }} />
        ) : null}
      </Box>

      <Box
        sx={{
          flex: { md: 1 },
          minHeight: 0,
          display: { xs: 'block', md: 'grid' },
          gridTemplateColumns: { md: '380px 1fr' },
          width: '100%',
        }}
      >
        <Box
          sx={{
            minHeight: 0,
            height: { md: '100%' },
            overflow: { xs: 'visible', md: 'auto' },
            borderRight: { md: '1px solid' },
            borderColor: { md: 'divider' },
            bgcolor: 'background.paper',
          }}
        >
          <PageTrafficPanel businessId={businessId} />
          <FileList
            businessId={businessId}
            files={files}
            metricsByPath={effMetricsByPath}
            selectedPath={selected?.path || ''}
            loading={loadingList}
            error={listError}
            onRefresh={refreshList}
            onOpenFile={openFile}
            onCreateInCollection={startCreate}
          />
        </Box>

        <Box
          sx={{
            minHeight: 0,
            height: { md: '100%' },
            overflow: { xs: 'visible', md: 'auto' },
          }}
        >
          <WelcomeChecklist />
          {isMobile ? (
            <MobileEditorPane {...editorProps} />
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { lg: 'minmax(0,1fr) 300px' },
                gap: 2,
                alignItems: 'start',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <EditorPane {...editorProps} />
              </Box>
              {mode === 'edit' && selected ? (
                <Box
                  sx={{
                    display: { xs: 'none', lg: 'block' },
                    position: { lg: 'sticky' },
                    top: { lg: 16 },
                    pr: { lg: 2 },
                  }}
                >
                  <EditorInsightsRail
                    file={selected}
                    metricsByPath={effMetricsByPath}
                    previewUrls={previewUrls}
                    publishStatus={publishStatus}
                    schemaFields={schemaFields}
                    frontmatterObj={frontmatterObj}
                    markdownBody={markdownBody}
                    canAutoFix={canAutoFix}
                    onApplyField={(field, value) =>
                      setFrontmatterObj(setAtPath(frontmatterObj, field, value))
                    }
                    onApplyBody={(value) => setMarkdownBody(value)}
                  />
                </Box>
              ) : null}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
